import { useCallback, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';

import { DynamicGridContext } from './_components/DynamicGridContext';
import { MIN_CARD_H, MIN_CARD_W, ROW_HEIGHT } from './_constants';
import { usePersistedLayout } from './_hooks/usePersistedLayout';
import type { DynamicGridProviderProps } from './types';

export const DynamicGridProvider = ({
  storageKey,
  items,
  defaultVisibleItems,
  rowHeight = ROW_HEIGHT,
  children,
}: DynamicGridProviderProps) => {
  // defaultLayoutRef and itemsRef capture initial values only — items are
  // expected to be stable. If items change structurally, bump storageKey to
  // force defaultLayout and defaultLayoutRef to reset.
  const defaultLayoutRef = useRef(
    items.map(({ id, x, y, w, h, minW = MIN_CARD_W, minH = MIN_CARD_H }) => ({
      i: id,
      x,
      y,
      w,
      h,
      minW: Math.max(minW, MIN_CARD_W),
      minH: Math.max(minH, MIN_CARD_H),
    }))
  );
  const defaultLayout = defaultLayoutRef.current;

  const itemsRef = useRef(items);
  const [isCustomizing, setIsCustomizing] = useState(false);

  const {
    layout,
    onLayoutChange,
    resetLayout,
    hiddenItems,
    toggleItemVisibility,
    revertToStoredState,
    commitDraft,
  } = usePersistedLayout({
    storageKey,
    defaultLayout,
    defaultVisibleItems,
    isDraft: isCustomizing,
  });

  const saveChanges = useCallback(() => {
    commitDraft();
    setIsCustomizing(false);
  }, [commitDraft]);

  const discardChanges = useCallback(() => {
    revertToStoredState();
    setIsCustomizing(false);
  }, [revertToStoredState]);

  const value = useMemo(
    () => ({
      rowHeight,
      layout,
      onLayoutChange,
      resetLayout,
      hiddenItems,
      toggleItemVisibility,
      items: itemsRef.current,
      isCustomizing,
      setIsCustomizing,
      saveChanges,
      discardChanges,
    }),
    [
      rowHeight,
      layout,
      onLayoutChange,
      resetLayout,
      hiddenItems,
      toggleItemVisibility,
      isCustomizing,
      saveChanges,
      discardChanges,
    ]
  );

  return (
    <DynamicGridContext.Provider value={value}>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        {children}
      </Box>
    </DynamicGridContext.Provider>
  );
};
